import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, Paper, Stack, Table, TableBody, TableCell,
  TableFooter, TableHead, TableRow, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { journalsApi, journalsWriteApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { StatusPill } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { useAuth } from "@/store/auth";
import { formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * UC-M1-ACC-01 — Journal entry detail. Read-only once Posted (DB trigger enforces
 * immutability on the lines). Draft journals can be Posted from here; posting is
 * blocked when the entry's date falls in a closed period.
 */
export function JournalEntryDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canPost = useAuth(s => s.hasPermission("accounting.journal.post"));
  const [postOpen, setPostOpen] = useState(false);

  const query = useQuery({
    queryKey: ["journal-entry", id],
    queryFn: () => journalsApi.get(id),
    enabled: !!id,
  });

  const postMutation = useMutation({
    mutationFn: () => journalsWriteApi.post(id, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journal-entry", id] });
      qc.invalidateQueries({ queryKey: ["journal-entries"] });
      setPostOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const je = query.data;
  const balanced = Math.abs(je.totalDebit - je.totalCredit) <= 0.01;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{je.entryNo}</Typography>
          <StatusPill label={je.status} />
          {je.isAutoGenerated && <Chip size="small" variant="outlined" label={`Auto · ${je.referenceType ?? "system"}`} />}
        </Stack>
        {je.status === "Draft" && canPost && (
          <Button variant="contained" color="success" startIcon={<PostAddIcon />}
            onClick={() => setPostOpen(true)}>
            Post journal
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Entry date</Typography>
            <Typography>{formatDate(je.entryDate)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Reference</Typography>
            <Typography>{je.referenceType ?? "Manual"}{je.referenceId ? ` · ${je.referenceId.slice(0, 8)}…` : ""}</Typography></Stack>
        </Stack>
        {je.description && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{je.description}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Account</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Debit</TableCell>
            <TableCell align="right">Credit</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {je.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.accountCode} — {l.accountName}</Typography>
                </TableCell>
                <TableCell>{l.description ?? "—"}</TableCell>
                <TableCell align="right">{l.debit > 0 ? inr.format(l.debit) : ""}</TableCell>
                <TableCell align="right">{l.credit > 0 ? inr.format(l.credit) : ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                <Chip size="small" color={balanced ? "success" : "error"}
                  label={balanced ? "Balanced" : `Off by ${inr.format(Math.abs(je.totalDebit - je.totalCredit))}`} />
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{inr.format(je.totalDebit)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{inr.format(je.totalCredit)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/accounting/journals")}>Back to list</Button>

      <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setPostOpen(false)}>Post journal {je.entryNo}?</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText>
            Posting locks the entry — the lines become immutable (enforced by the DB trigger).
            Posting is blocked when the entry date falls in a closed period; corrections then
            require a reversing entry in the open period.
          </DialogContentText>
          {postMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={!balanced || postMutation.isPending}
            startIcon={postMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => postMutation.mutate()}>
            Post
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
